// @givmo/api-client — the interceptor stack, ported from the Dio chain as
// @hey-api/client-fetch middleware.
//
// The generated fetch client exposes `client.interceptors.{request,response,error}`
// (see src/generated/client/client.gen.ts). Each interceptor receives the native
// `Request`/`Response` and returns a (possibly new) one. Native `Request`/
// `Response` headers are immutable post-construction, so header mutation clones.
//
// Order matches the Dio registration:
//   request:  correlation (X-Request-Id, first — independent of auth) →
//             Bearer ID token → standard/route-selected limited-use X-Firebase-AppCheck
//   response: optional user-reauthenticated one-shot replay → paginated-envelope unwrap
//             (shape-gated, body-rewriting)
//   error:    transport/HTTP → GivmoError mapping + PII-safe logging
//
// PII invariant: the logger only ever receives metadata (method, path, status,
// correlation id, error kind); request/response bodies and token header values
// never reach it.

import { generateRequestId, REQUEST_ID_HEADER, type RequestIdPrefix } from '../correlation';
import {
  type GivmoClientConfig,
  type FirebaseAuthProvider,
  type FirebaseAppCheckProvider,
  type GivmoRequestDescriptor,
  type LimitedUseAppCheckPolicy,
  resolveBaseUrl,
} from '../config';
import {
  firstErrorCode,
  GivmoError,
  LimitedUseAppCheckError,
  mapLimitedUseAppCheckError,
  mapReauthReplayError,
  mapResponseError,
  mapTransportError,
  parseErrorEnvelope,
  ReauthReplayError,
} from '../errors';
import { createConsoleLogger, logSafePath, noopLogger, type NetworkLogger } from '../logging';
import { isSameOriginAsBase } from './same-origin';
import { isPaginatedEnvelope } from './envelope';
import type { Client } from '../generated/client';

const APP_CHECK_HEADER = 'X-Firebase-AppCheck';
const AUTHORIZATION_HEADER = 'Authorization';
const REAUTH_REQUIRED_ERROR_CODE = 'reauth_required';
const MONEY_RATE_LIMITED_ERROR_CODE = 'superadmin_money_rate_limited';

/** A handle on what the middleware stamped, for tests + the forced-refresh hook. */
export interface InstalledMiddleware {
  readonly baseUrl: string;
  readonly prefix: RequestIdPrefix;
}

/** Reads the client-stamped correlation id back off a request/options pair. */
function stampedRequestId(request: Request | undefined): string | null {
  return request?.headers.get(REQUEST_ID_HEADER) ?? null;
}

/** Clone a Request, adding/overwriting a single header (Request headers are immutable). */
function withHeader(request: Request, name: string, value: string): Request {
  const headers = new Headers(request.headers);
  headers.set(name, value);
  return new Request(request, { headers });
}

function requestDescriptor(request: Request): GivmoRequestDescriptor {
  return {
    method: request.method,
    path: new URL(request.url).pathname,
  };
}

function matchesLimitedUsePolicy(
  request: Request,
  policy: LimitedUseAppCheckPolicy | undefined,
): boolean {
  return policy?.matches(requestDescriptor(request)) === true;
}

async function responseHasErrorCode(response: Response, errorCode: string): Promise<boolean> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('json')) return false;
  try {
    const entries = parseErrorEnvelope(await response.clone().json());
    return entries.some((entry) => entry.errorCode === errorCode);
  } catch {
    return false;
  }
}

function withRetryAfterMessage(error: GivmoError, retryAfter: string | null): GivmoError {
  if (
    error.status !== 429 ||
    firstErrorCode(error) !== MONEY_RATE_LIMITED_ERROR_CODE ||
    retryAfter === null
  ) {
    return error;
  }

  const seconds = /^\d+$/.test(retryAfter) ? Number(retryAfter) : null;
  const instruction =
    seconds === null
      ? Number.isNaN(Date.parse(retryAfter))
        ? null
        : `Please try again after ${retryAfter}.`
      : `Please try again in ${seconds} ${seconds === 1 ? 'second' : 'seconds'}.`;
  if (instruction === null) return error;

  return new GivmoError({
    message: `${error.message} ${instruction}`,
    kind: error.kind,
    status: error.status,
    entries: error.entries,
    requestId: error.requestId,
    connectCode: error.connectCode,
    connectType: error.connectType,
  });
}

/**
 * Install the Givmo interceptor stack onto a generated fetch client. Idempotent
 * per call site is NOT guaranteed — call once per client during configuration.
 */
export function installMiddleware(client: Client, config: GivmoClientConfig): InstalledMiddleware {
  const baseUrl = resolveBaseUrl(config.baseUrl);
  const prefix = config.appPrefix;
  const logger: NetworkLogger = config.debugLogEnabled
    ? createConsoleLogger(prefix, true)
    : noopLogger;
  // A request body is consumed by the first fetch. Keep an unused clone only for policy-matched
  // requests so a successful user reauthentication can replay that exact operation once.
  const limitedUseRetryTemplates = new WeakMap<Request, Request>();

  // ---- request: correlation id (first; independent of auth) ----
  client.interceptors.request.use((request) => {
    try {
      const id = generateRequestId(prefix, config.requestIdRng);
      const stamped = withHeader(request, REQUEST_ID_HEADER, id);
      logger.log({
        phase: 'request',
        method: stamped.method,
        path: logSafePath(stamped.url),
        requestId: id,
      });
      return stamped;
    } catch {
      // The interceptor is in the hot path: a logging/generation bug must never
      // block outbound traffic (ports the Dio try/catch + unconditional next).
      return request;
    }
  });

  // ---- request: Bearer Firebase ID token (same-origin only) ----
  if (config.auth) {
    const auth: FirebaseAuthProvider = config.auth;
    client.interceptors.request.use(async (request) => {
      if (!isSameOriginAsBase(request.url, baseUrl)) return request;
      // Respect a caller-supplied Authorization (e.g. a forced-refresh token
      // for a long download — see capabilities.forceRefreshAuthHeader). This is
      // the `putIfAbsent` semantics the Dio TokenInterceptor effectively wants
      // for the freshness-critical paths; the common path has no header set.
      if (request.headers.has(AUTHORIZATION_HEADER)) return request;
      try {
        const token = await auth.getIdToken();
        if (token) return withHeader(request, AUTHORIZATION_HEADER, `Bearer ${token}`);
      } catch {
        // Don't block the request — the backend answers 401 and the error
        // interceptor maps it. (No token detail is logged.)
      }
      return request;
    });
  }

  // ---- request: X-Firebase-AppCheck (same-origin only) ----
  if (config.appCheck || config.limitedUseAppCheck) {
    const appCheck: FirebaseAppCheckProvider | undefined = config.appCheck;
    client.interceptors.request.use(async (request) => {
      if (!isSameOriginAsBase(request.url, baseUrl)) return request;

      if (matchesLimitedUsePolicy(request, config.limitedUseAppCheck)) {
        let token: string;
        try {
          if (appCheck?.getLimitedUseToken === undefined) {
            throw new Error(
              'A limited-use App Check route matched without a limited-use token provider.',
            );
          }
          // Deliberately no fallback: replay-protected requests fail closed if a fresh token cannot
          // be minted, and MUST NOT fall back to the cached standard token.
          const mintedToken = await appCheck.getLimitedUseToken();
          if (!mintedToken) {
            throw new Error('Limited-use App Check returned an empty token (fail-closed).');
          }
          token = mintedToken;
        } catch {
          throw new LimitedUseAppCheckError();
        }
        const attested = withHeader(request, APP_CHECK_HEADER, token);
        // Terminal-interceptor invariant: App Check is registered LAST in the request chain, so
        // `attested` is the same object identity that reaches the response interceptor and keys this
        // WeakMap. A later replacement is a deliberate fail-safe miss: replaying this stashed clone
        // would drop the trailing interceptor's modifications, so the original 401 must surface.
        limitedUseRetryTemplates.set(attested, attested.clone());
        return attested;
      }

      if (appCheck === undefined) return request;
      try {
        const token = await appCheck.getToken();
        if (token) return withHeader(request, APP_CHECK_HEADER, token);
      } catch {
        // App Check failures must not block; staging/prod return 401 if enforced.
      }
      return request;
    });
  }

  // ---- response: paginated-envelope unwrap (shape-gated, body-rewriting) ----
  client.interceptors.response.use(async (response, request, options) => {
    let handledResponse = response;
    const isLimitedUse = matchesLimitedUsePolicy(request, config.limitedUseAppCheck);
    // Terminal-interceptor invariant: App Check is registered LAST, so the final sent Request is
    // normally the WeakMap key. A miss is deliberately fail-safe, not a bug: if a trailing
    // interceptor replaced it, the stashed clone is not what was sent and blind replay would lose
    // those modifications; skip auto-retry and surface the 401 for operator reauth + re-click.
    const retryTemplate = isLimitedUse ? limitedUseRetryTemplates.get(request) : undefined;
    limitedUseRetryTemplates.delete(request);

    if (
      isLimitedUse &&
      retryTemplate !== undefined &&
      handledResponse.status === 401 &&
      config.limitedUseAppCheck?.onReauthenticationRequired !== undefined &&
      (await responseHasErrorCode(handledResponse, REAUTH_REQUIRED_ERROR_CODE))
    ) {
      // Metadata only (method/path/id/marker class), like every other line: a silent drop after a
      // successful reauthentication is what this log exists to make visible in non-prod consoles.
      const logPostReauthFailure = (errorKind: string) =>
        logger.log({
          phase: 'error',
          method: request.method,
          path: logSafePath(request.url),
          status: 'no-response',
          requestId: stampedRequestId(request) ?? 'missing',
          errorKind,
        });

      try {
        // The app handler opens a dialog and settles only after its button drives a fresh sign-in.
        // Rejection (including operator cancellation) leaves the original 401 to flow normally.
        await config.limitedUseAppCheck.onReauthenticationRequired();
      } catch {
        // The operator declined, or could not, re-prove: no retry, and the original canonical
        // reauth_required response remains the normal surfaced error.
        return handledResponse;
      }

      // From here the operator HAS re-verified. Nothing below may fall back to the original 401 —
      // that would tell them to re-authenticate again when what failed was the resend.
      let headers: Headers;
      try {
        const freshIdToken = await config.auth?.getIdToken(true);
        const freshAppCheckToken = await config.appCheck?.getLimitedUseToken?.();
        if (!freshIdToken || !freshAppCheckToken) {
          throw new Error('Fresh tokens were not minted after reauthentication (fail-closed).');
        }
        headers = new Headers(retryTemplate.headers);
        headers.set(AUTHORIZATION_HEADER, `Bearer ${freshIdToken}`);
        headers.set(APP_CHECK_HEADER, freshAppCheckToken);
      } catch {
        // Fresh proof could not be minted after a successful reauthentication — the same fail-closed
        // attestation failure the request interceptor raises, mapped by the error interceptor.
        const failure = new LimitedUseAppCheckError();
        logPostReauthFailure(failure.name);
        throw failure;
      }

      try {
        const retryRequest = new Request(retryTemplate, { headers });

        // Direct fetch is intentional: it replays the original serialized body/idempotency headers
        // but does not re-enter the response interceptor, enforcing retry-once semantics.
        //
        // Called UNBOUND, deliberately. `options.fetch` is the native `fetch` unless an app
        // configured one, and invoking it as a method of the options object rebinds `this` to that
        // object — every browser rejects that with "Illegal invocation" before anything is sent.
        // The generated client's own `const _fetch = opts.fetch!` (client.gen.ts) exists for the
        // same reason; the strict-`this` stub in index.test.ts pins it here.
        const replayFetch = options.fetch ?? globalThis.fetch;
        handledResponse = await replayFetch(retryRequest);
      } catch {
        // The replay produced no response. That is NOT proof the server never received it — a
        // connection dropped after the body was sent rejects exactly like a refused one — so the
        // surfaced copy says "no response, check before retrying", never "nothing was performed".
        // What it must never be is the stale 401.
        const failure = new ReauthReplayError();
        logPostReauthFailure(failure.name);
        throw failure;
      }
    }

    // Only attempt unwrap on JSON; binary/text responses pass through untouched
    // so the ZIP/PDF/HTML download paths are never consumed here.
    const contentType = handledResponse.headers.get('Content-Type') ?? '';
    if (!contentType.includes('json')) return handledResponse;
    let body: unknown;
    try {
      // Clone so we don't consume the body the client will read.
      body = await handledResponse.clone().json();
    } catch {
      return handledResponse; // non-JSON-parseable body — leave it for the client.
    }
    if (!isPaginatedEnvelope(body)) return handledResponse;
    logger.log({
      phase: 'response',
      method: request.method,
      path: logSafePath(request.url),
      status: handledResponse.status,
      requestId: stampedRequestId(request) ?? 'missing',
    });
    // Rewrite the body to the unwrapped `data`, preserving status + headers.
    const headers = new Headers(handledResponse.headers);
    headers.delete('Content-Length'); // length changed after rewrite
    return new Response(JSON.stringify(body.data), {
      status: handledResponse.status,
      statusText: handledResponse.statusText,
      headers,
    });
  });

  // ---- error: transport/HTTP -> GivmoError + PII-safe logging ----
  client.interceptors.error.use(async (error, response, request) => {
    const stamped = stampedRequestId(request);
    let mapped: GivmoError;
    // The two markers are checked BEFORE the response branch: thrown by the response interceptor
    // after a successful reauthentication, they arrive with the ORIGINAL reauth_required 401 still
    // attached as `response`, and mapping that would tell the operator to re-authenticate again.
    if (error instanceof ReauthReplayError) {
      mapped = mapReauthReplayError({ stampedRequestId: stamped });
    } else if (error instanceof LimitedUseAppCheckError) {
      // Raised before fetch by the request interceptor (no response) or after reauthentication by
      // the response interceptor (the original 401 attached): either way a fail-closed mint.
      mapped = mapLimitedUseAppCheckError({ stampedRequestId: stamped });
    } else if (response) {
      // A response was produced: `error` is the parsed JSON body (or text the
      // client could not JSON-parse). The body shape gate lives in the mapper.
      mapped = mapResponseError({
        status: response.status,
        body: error,
        stampedRequestId: stamped,
        responseHeader: response.headers.get('x-request-id'),
      });
      if (request !== undefined && matchesLimitedUsePolicy(request, config.limitedUseAppCheck)) {
        mapped = withRetryAfterMessage(mapped, response.headers.get('Retry-After'));
      }
    } else {
      // No response: a transport failure (timeout/offline/abort/DNS).
      mapped = mapTransportError({ stampedRequestId: stamped });
    }
    logger.log({
      phase: 'error',
      method: request?.method ?? 'UNKNOWN',
      path: request ? logSafePath(request.url) : 'unknown',
      // The status of the error being SURFACED — identical to the response's for every mapped
      // response, and `no-response` for the markers above (whose attached 401 is not what surfaces).
      status: mapped.status ?? 'no-response',
      requestId: stamped ?? 'missing',
      errorKind: mapped.name,
    });
    // Returning the mapped error makes it the `error` field of the result (and
    // the thrown value when throwOnError is on).
    return mapped;
  });

  return { baseUrl, prefix };
}
